import React from "react";
import { TEAL } from "./constants";
import { seriesColor } from "./charts";

const inputBase = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 12,
  padding: "4px 6px",
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 6,
  background: "white",
  color: "#1E293B",
  width: "100%",
  boxSizing: "border-box",
};

function Field({ value, onChange, numeric, ...rest }) {
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(numeric ? e.target.value.replace(/[^0-9.-]/g, "") : e.target.value)}
      style={{ ...inputBase, textAlign: numeric ? "right" : "left", fontVariantNumeric: "tabular-nums" }}
      {...rest}
    />
  );
}

export default function ChartDataEditor({ el, onChange, onClose }) {
  if (!el || el.type !== "chart") return null;

  const data = el.data || {};
  const patch = (next) => onChange(el.id, { ...data, ...next });

  const isTable = el.chartType === "table";
  const isTimeline = el.chartType === "timeline";
  const single = el.chartType === "pie" || el.chartType === "donut" || el.chartType === "column";

  const setCategory = (i, v) => {
    const categories = [...(data.categories || [])];
    categories[i] = v;
    patch({ categories });
  };

  const setValue = (si, ci, v) => {
    const series = (data.series || []).map((s, i) =>
      i !== si ? s : { ...s, values: Object.assign([...(s.values || [])], { [ci]: v === "" ? 0 : Number(v) }) }
    );
    patch({ series });
  };

  const setSeriesName = (si, v) => {
    const series = (data.series || []).map((s, i) => (i === si ? { ...s, name: v } : s));
    patch({ series });
  };

  const addRow = () => {
    if (isTable) {
      patch({ rows: [...(data.rows || []), (data.columns || []).map(() => "")] });
      return;
    }
    if (isTimeline) {
      patch({ events: [...(data.events || []), { label: "New step", when: "" }] });
      return;
    }
    patch({
      categories: [...(data.categories || []), `Item ${(data.categories?.length || 0) + 1}`],
      series: (data.series || []).map((s) => ({ ...s, values: [...(s.values || []), 0] })),
    });
  };

  const removeRow = (i) => {
    if (isTable) { patch({ rows: (data.rows || []).filter((_, r) => r !== i) }); return; }
    if (isTimeline) { patch({ events: (data.events || []).filter((_, r) => r !== i) }); return; }
    patch({
      categories: (data.categories || []).filter((_, c) => c !== i),
      series: (data.series || []).map((s) => ({ ...s, values: (s.values || []).filter((_, c) => c !== i) })),
    });
  };

  const addSeries = () => {
    const count = (data.categories || []).length;
    patch({
      series: [...(data.series || []), { name: `Series ${(data.series?.length || 0) + 1}`, values: Array(count).fill(0) }],
    });
  };

  const rowCount = isTable ? (data.rows || []).length
    : isTimeline ? (data.events || []).length
    : (data.categories || []).length;

  return (
    <div
      className="absolute right-3 bottom-3 z-[60] rounded-xl border border-black/[0.08] shadow-[0_10px_30px_rgba(0,0,0,0.18)] font-['DM_Sans']"
      style={{ background: "#FFFFFF", width: 340, maxHeight: "62%", display: "flex", flexDirection: "column" }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-black/[0.06] flex-shrink-0">
        <div>
          <div className="text-[12px] font-bold text-slate-800 leading-none">Edit values</div>
          <div className="text-[10px] text-slate-400 pt-[3px] leading-none">{el.title}</div>
        </div>
        <button type="button" onClick={onClose} title="Close"
          className="text-slate-400 text-[16px] leading-none bg-transparent border-none cursor-pointer px-1">
          ✕
        </button>
      </div>

      <div className="overflow-y-auto px-3 py-2" style={{ minHeight: 0 }}>
        <label className="block text-[10px] font-semibold text-slate-500 pb-1">Title</label>
        <input
          value={el.title || ""}
          onChange={(e) => onChange(el.id, undefined, { title: e.target.value })}
          style={{ ...inputBase, marginBottom: 10 }}
        />

        {!isTable && !isTimeline && (
          <>
            {(data.series || []).map((s, si) => (
              <div key={si} className="flex items-center gap-2 pb-[6px]">
                <span className="w-[10px] h-[10px] rounded-[3px] flex-shrink-0"
                  style={{ background: seriesColor(si) }} />
                <Field value={s.name} onChange={(v) => setSeriesName(si, v)} />
              </div>
            ))}

            <div className="grid gap-[4px] pt-1"
              style={{ gridTemplateColumns: `1fr ${(data.series || []).map(() => "62px").join(" ")} 20px` }}>
              <div className="text-[10px] font-semibold text-slate-500">Label</div>
              {(data.series || []).map((s, si) => (
                <div key={si} className="text-[10px] font-semibold text-slate-500 text-right">
                  {s.name || `S${si + 1}`}
                </div>
              ))}
              <div />
              {(data.categories || []).map((c, ci) => (
                <React.Fragment key={ci}>
                  <Field value={c} onChange={(v) => setCategory(ci, v)} />
                  {(data.series || []).map((s, si) => (
                    <Field key={si} numeric value={s.values?.[ci]} onChange={(v) => setValue(si, ci, v)} />
                  ))}
                  <button type="button" onClick={() => removeRow(ci)} title="Remove row"
                    className="text-slate-300 hover:text-red-500 text-[13px] bg-transparent border-none cursor-pointer">
                    ×
                  </button>
                </React.Fragment>
              ))}
            </div>
          </>
        )}

        {isTable && (
          <div className="grid gap-[4px]"
            style={{ gridTemplateColumns: `${(data.columns || []).map(() => "1fr").join(" ")} 20px` }}>
            {(data.columns || []).map((c, ci) => (
              <Field key={`c${ci}`} value={c} onChange={(v) => {
                const columns = [...(data.columns || [])];
                columns[ci] = v;
                patch({ columns });
              }} />
            ))}
            <div />
            {(data.rows || []).map((row, ri) => (
              <React.Fragment key={ri}>
                {row.map((cell, ci) => (
                  <Field key={ci} value={cell} onChange={(v) => {
                    const rows = (data.rows || []).map((r, i) =>
                      i !== ri ? r : Object.assign([...r], { [ci]: v })
                    );
                    patch({ rows });
                  }} />
                ))}
                <button type="button" onClick={() => removeRow(ri)} title="Remove row"
                  className="text-slate-300 hover:text-red-500 text-[13px] bg-transparent border-none cursor-pointer">
                  ×
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {isTimeline && (
          <div className="grid gap-[4px]" style={{ gridTemplateColumns: "1fr 90px 20px" }}>
            <div className="text-[10px] font-semibold text-slate-500">Step</div>
            <div className="text-[10px] font-semibold text-slate-500">When</div>
            <div />
            {(data.events || []).map((ev, i) => (
              <React.Fragment key={i}>
                <Field value={ev.label} onChange={(v) => {
                  const events = (data.events || []).map((e, j) => (j === i ? { ...e, label: v } : e));
                  patch({ events });
                }} />
                <Field value={ev.when} onChange={(v) => {
                  const events = (data.events || []).map((e, j) => (j === i ? { ...e, when: v } : e));
                  patch({ events });
                }} />
                <button type="button" onClick={() => removeRow(i)} title="Remove step"
                  className="text-slate-300 hover:text-red-500 text-[13px] bg-transparent border-none cursor-pointer">
                  ×
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 px-3 py-2 border-t border-black/[0.06] flex-shrink-0">
        <button type="button" onClick={addRow}
          className="text-[11px] font-semibold px-2 py-[5px] rounded-md cursor-pointer border"
          style={{ color: TEAL, borderColor: "rgba(77,184,181,0.4)", background: "rgba(77,184,181,0.10)" }}>
          + {isTimeline ? "Step" : "Row"}
        </button>
        {!isTable && !isTimeline && !single && (
          <button type="button" onClick={addSeries}
            className="text-[11px] font-semibold px-2 py-[5px] rounded-md cursor-pointer border border-black/10 bg-transparent text-slate-500">
            + Series
          </button>
        )}
        <span className="ml-auto text-[10px] text-slate-400">{rowCount} rows</span>
      </div>
    </div>
  );
}
